import React, { Fragment, useEffect, useRef, useState } from 'react';
import Page, { usePage } from '../../components/Page';
import { Button, Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import { useStyles } from '../../hooks/useStyles';
import { useAuth } from '../../units/auth/AuthProvider';
import { useGlobal } from '../../units/global/GlobalProvider';
import moment from 'moment';
import { washMicroSeconds } from '../../data/WashTime';
import { TimelineArray } from '../../data/Timeline';
import { api } from '../../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarClock, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { ToReservationDialog, useToReservationDialog } from './ToReservationDialog';
import { useScreenSize } from '../../hooks/useScreenSize';
import { CarQueue } from '../../api/car-queue';
import { grey } from '@mui/material/colors';
import { Marquee } from '../../components/Marquee';
import { Store } from '../../api/store';
import { Closed } from '../../api/closed';
import mqtt, { MqttClient } from 'mqtt';

export const useSimpleReservationPage = () => {
    const page = usePage();
    const classes = useStyles();
    const { userGroups, setUserGroups, reloadUserGroups } = useGlobal();
    const auth = useAuth();
    const screenSize = useScreenSize();
    const today = useRef(moment(new Date().getTime()).format('YYYY/MM/DD'));
    const openTimelineArray = useRef(
        new TimelineArray([
            {
                st: new Date(`${today.current} 00:00:00`).getTime(),
                ed: new Date(`${today.current} 09:30:00`).getTime(),
                status: `close`,
            },
            {
                st: new Date(`${today.current} 09:30:00`).getTime(),
                ed: new Date(`${today.current} 20:30:00`).getTime(),
                status: `available`,
            },
            {
                st: new Date(`${today.current} 20:30:00`).getTime(),
                ed: new Date(`${today.current} 00:00:00`).getTime() + 24 * 60 * 60 * 1000,
                status: `close`,
            },
        ]),
    );
    const [selectedReservationTime, setSelectedReservationTime] = useState<number | null>(null);
    useEffect(() => {
        if (selectedReservationTime) toReservationDialog.setInputEstimateTime(new Date(selectedReservationTime));
    }, [selectedReservationTime]);
    const activeThumb = useRef<number>(0);
    const timelineArrayRef = useRef<TimelineArray | null>(null);
    const reservationArrayRef = useRef<TimelineArray | null>(null);
    const [carQueues, setCarQueues] = useState<CarQueue[]>([]);
    const [stores, setStores] = useState<Store[]>([]);
    const [selectedStore, setSelectedStore] = useState<string>('default');
    const selectedStoreRef = useRef(selectedStore);
    useEffect(() => {
        selectedStoreRef.current = selectedStore;
    }, [selectedStore]);
    const [mqttClient, setMqttClient] = useState<MqttClient | null>(null);
    const mqttClientRef = useRef(mqttClient);
    useEffect(() => {
        mqttClientRef.current = mqttClient;
        if (!mqttClient) {
            const mqttClient = mqtt.connect(process.env.REACT_APP_MQTT_BROKER_URL!);
            setMqttClient(mqttClient);
        } else {
            mqttClient.on('connect', () => {
                console.log('Connected to MQTT broker');
            });
            mqttClient.on('error', (err) => {
                console.error('MQTT Client Error:', err);
                mqttClient.end();
            });
            mqttClient.on('reconnect', () => {
                console.log('Reconnecting to MQTT broker');
            });
            mqttClient.on('message', (topic, message) => {
                console.log(`Message received on topic ${topic}:`, message.toString());
                refresh();
            });
            mqttClient.subscribe('database/table/car_queue/#', { qos: 0 }, (error) => {
                if (error) {
                    console.error('Subscribe to topics error:', error);
                    return;
                }
                console.log('Subscribed to topic: database/table/car_queue/#');
            });
        }
        return () => {
            if (mqttClient) {
                mqttClient.end();
                console.log('Disconnected to MQTT broker');
            }
        };
    }, [mqttClient]);
    const [closeds, setCloseds] = useState<Closed[]>([]);
    useEffect(() => {
        api.closed
            .get({
                closedFmSt: new Date(new Date(moment().format('YYYY/MM/DD')).getTime() + 0 * 60 * 60 * 1000),
                closedFmEd: new Date(new Date(moment().format('YYYY/MM/DD')).getTime() + 24 * 60 * 60 * 1000),
                store: { storeId: selectedStore },
            })
            .then((ret) => setCloseds(ret.data || []));
    }, [selectedStore]);

    const reloadTimelineArray = async () => {
        await api.carQueue
            .get({
                complete: 'N',
                cancel: 'N',
                estimateTimeSt: new Date(new Date(moment().format('YYYY/MM/DD')).getTime() + 0 * 60 * 60 * 1000),
                estimateTimeEd: new Date(new Date(moment().format('YYYY/MM/DD')).getTime() + 7 * 24 * 60 * 60 * 1000),
                store: { storeId: selectedStoreRef.current },
            })
            .then((ret) => {
                reservationArrayRef.current = new TimelineArray(
                    (ret.data || []).map((carQueue) => ({
                        st: new Date(carQueue.estimateTime!).getTime(),
                        ed: new Date(carQueue.estimateTime!).getTime() + washMicroSeconds,
                        status: 'unavailable',
                    })),
                );
            });
    };
    const toReservationDialog = useToReservationDialog();
    const reloadMyCarQueues = async () => {
        if (auth.isAuth) {
            const myCarIds = await api.user.get({ userId: auth.profile.userId, relations: { cars: true } }).then((ret) => {
                return (ret.data || [])
                    .map((user) => user.cars || [])
                    .flat()
                    .map((car) => car.carId!);
            });
            const newCarQueues = await api.carQueue
                .get({
                    complete: 'N',
                    cancel: 'N',
                    estimateTimeSt: new Date(new Date(moment().format('YYYY/MM/DD')).getTime() + 0 * 60 * 60 * 1000),
                    estimateTimeEd: new Date(new Date(moment().format('YYYY/MM/DD')).getTime() + 7 * 24 * 60 * 60 * 1000),
                    store: { storeId: selectedStoreRef.current },
                    order: { estimateTime: 'asc' },
                })
                .then((ret) => (ret.data || []).filter((carQueue) => myCarIds.includes(carQueue.carId!)));
            setCarQueues(newCarQueues);
        }
    };
    const reloadStores = async () => {
        const newStores = await api.store.get({}).then((ret) => ret.data || []);
        setStores(newStores);
        if (newStores.length == 1) setSelectedStore(newStores[0].storeId!);
    };

    useEffect(() => {
        reloadTimelineArray();
        reloadMyCarQueues();
    }, [selectedStore]);

    useEffect(() => {
        if (reservationArrayRef.current != null) {
            timelineArrayRef.current = openTimelineArray.current
                .cutByTimelines(
                    new TimelineArray([
                        {
                            st: new Date(`${today.current} 09:30:00`).getTime(),
                            ed: new Date(moment().format('YYYY/MM/DD HH:mm')).getTime() + 60 * 1000,
                            status: 'close',
                        },
                        ...closeds.map((closed) => ({
                            st: new Date(moment(closed.closedFm).format('YYYY/MM/DD HH:mm')).getTime(),
                            ed: new Date(moment(closed.closedTo).format('YYYY/MM/DD HH:mm')).getTime(),
                            status: 'close' as any,
                        })),
                    ]),
                )
                .cutByTimelines(new TimelineArray(reservationArrayRef.current.getTimelines().filter((tl) => tl.ed > new Date().getTime())))
                .sort();

            const nextAvailableTimeline = timelineArrayRef.current
                .getTimelines()
                .filter((tl) => tl.status == 'available')
                .filter((tl) => tl.ed - tl.st >= washMicroSeconds)
                .sort((a, b) => a.st - b.st)
                .find((tl) => tl);

            if (nextAvailableTimeline) {
                setSelectedReservationTime(nextAvailableTimeline.st);
                page.simpleAlert.show({ autoHideDuration: 2000, severity: 'warning', message: '預約時間更新' });
            } else {
                setSelectedReservationTime(null);
            }
        }
    }, [
        JSON.stringify(reservationArrayRef.current ? reservationArrayRef.current.getTimelines() : null),
        moment().format('YYYY/MM/DD HH:mm'),
        closeds,
    ]);
    // const timeRef = useRef(moment().format('YYYY/MM/DD HH:mm'));
    const refresh = async () => {
        reloadTimelineArray();
        reloadUserGroups();
        reloadMyCarQueues();
        reloadStores();
    };
    useEffect(() => {
        reloadTimelineArray();
        reloadUserGroups();
        reloadMyCarQueues();
        reloadStores();
        const interval = setInterval(() => {
            if (mqttClientRef.current == null || !mqttClientRef.current.connected) refresh();
        }, 10 * 1000);
        return () => clearInterval(interval);
    }, []);
    // useEffect(() => {
    //     const interval = setInterval(() => {
    //         console.log(timeRef.current, moment().format('YYYY/MM/DD HH:mm'), timeRef.current != moment().format('YYYY/MM/DD HH:mm'));
    //         if (timeRef.current != moment().format('YYYY/MM/DD HH:mm')) {
    //             refresh();
    //             timeRef.current = moment().format('YYYY/MM/DD HH:mm');
    //         }
    //     }, 1 * 1000);
    //     return () => clearInterval(interval);
    // }, []);
    return {
        page,
        classes,
        auth,
        screenSize,
        alert,
        open,
        selectedReservationTime,
        setSelectedReservationTime,
        activeThumb,
        carQueues,
        setCarQueues,
        stores,
        setStores,
        selectedStore,
        setSelectedStore,
        closeds,
        setCloseds,
        reloadTimelineArray,
        toReservationDialog,
        reloadMyCarQueues,
        reloadStores,
    };
};

interface SimpleReservationPageProps {}

export const SimpleReservationPage = (SimpleReservationPageProps: SimpleReservationPageProps): React.ReactElement => {
    const props = useSimpleReservationPage();
    const rowStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderCollapse: 'collapse',
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
        height: 44,
    };

    return (
        <Page {...props.page} title={'候位洗車'}>
            <Grid container flexDirection={'row'} height={'100%'} overflow={'hidden'} sx={{ userSelect: 'none' }}>
                <Grid
                    container
                    item
                    flex={1}
                    flexDirection={'column'}
                    padding={props.screenSize.mdUp ? 4 : 1}
                    height={'100%'}
                    overflow={'hidden'}
                    sx={{ backgroundColor: grey[100] }}
                >
                    <Grid container item flexDirection={'row'}>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-start'} xs={12} sx={{ userSelect: 'none' }}>
                            {props.closeds.length > 0 ? (
                                <Grid container item flexDirection={'row'} xs={12} sx={{ mb: 2 }}>
                                    <Marquee text={props.closeds.map((closed) => closed.msg).join(', ')} />
                                </Grid>
                            ) : (
                                <></>
                            )}
                            <Grid container item flexDirection={'row'} xs={8} md={6}>
                                <Select
                                    value={props.selectedStore}
                                    onChange={(e) => props.setSelectedStore(e.target.value)}
                                    fullWidth
                                    style={{ margin: 4, marginTop: 12, backgroundColor: 'white' }}
                                >
                                    <MenuItem value={'default'} disabled>
                                        {'請選擇分店：'}
                                    </MenuItem>
                                    {props.stores.map((store) => (
                                        <MenuItem key={store.storeId} value={store.storeId}>
                                            {store.storeName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                            <Grid container item flexDirection={'row'} xs={4} md={4}>
                                <Button
                                    disabled={
                                        ![
                                            props.selectedReservationTime != null,
                                            props.carQueues.length == 0,
                                            props.selectedStore != 'default',
                                        ].every((v) => v)
                                    }
                                    className={props.classes.buttonCyan}
                                    fullWidth
                                    style={{ margin: 4, marginTop: 12 }}
                                    onClick={() => {
                                        props.toReservationDialog.setInputCarQueueId(undefined);
                                        props.toReservationDialog.setInputCarIds([]);
                                        props.toReservationDialog.setInputMaxCarNumber(1);
                                        props.toReservationDialog.setInputEstimateTime(new Date(props.selectedReservationTime!));
                                        props.toReservationDialog.setOnConfirm(
                                            () => (inputCarIds: string[], inputEstimateTime: Date | undefined) => {
                                                api.carQueue
                                                    .createBulk(
                                                        inputCarIds.map((carId, index, array) => ({
                                                            carId: carId,
                                                            estimateTime: new Date(
                                                                new Date(inputEstimateTime!).getTime() + index * washMicroSeconds,
                                                            ),
                                                            store: { storeId: props.selectedStore },
                                                        })),
                                                    )
                                                    .then((ret) => {
                                                        if (ret.success) {
                                                            props.reloadTimelineArray();
                                                            props.reloadMyCarQueues();
                                                            props.toReservationDialog.onClose();
                                                            props.page.simpleAlert?.show({
                                                                autoHideDuration: 2000,
                                                                severity: 'success',
                                                                message: ret.message,
                                                            });
                                                        } else {
                                                            props.reloadTimelineArray();
                                                            props.reloadMyCarQueues();
                                                            props.toReservationDialog.onClose();
                                                            props.page.simpleAlert?.show({
                                                                autoHideDuration: 6000,
                                                                severity: 'error',
                                                                message: typeof ret.message == 'string' ? ret.message : JSON.stringify(ret),
                                                            });
                                                        }
                                                    });
                                            },
                                        );
                                        props.toReservationDialog.onOpen();
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlus} fixedWidth />
                                    {'我要預約'}
                                </Button>
                            </Grid>
                            <Grid container item flexDirection={'row'} xs={0} md={4}></Grid>
                        </Grid>
                        <Grid
                            container
                            item
                            flex={1}
                            padding={props.screenSize.mdUp ? 2 : 1}
                            borderRadius={2}
                            marginTop={2}
                            overflow={'hidden'}
                            sx={{ height: '100%', backgroundColor: 'white' }}
                        >
                            <Typography
                                component={'div'}
                                flex={1}
                                borderRadius={2}
                                sx={{
                                    display: 'grid',
                                    placeContent: 'start',
                                    gridTemplateColumns: ['minmax(90px, 3fr)', 'minmax(180px, 6fr)', '120px'].join(' '),
                                    gap: 0,
                                    borderCollapse: 'collapse',
                                    overflow: 'auto',
                                    maxHeight: '100%',
                                    backgroundColor: 'white',
                                    border: '1px solid rgba(224, 224, 224, 1)',
                                    '& > div:nth-child(-n + 2)': {
                                        position: 'relative',
                                    },
                                    '& > div:nth-child(-n + 2)::after': {
                                        content: '""',
                                        position: 'absolute',
                                        top: '33%',
                                        height: '33%',
                                        width: 0,
                                        borderRight: '2px solid rgba(0, 0, 0, 0.1)',
                                        right: 0,
                                    },
                                    '& > div:nth-last-child(-n + 3)': { border: 0 },
                                }}
                            >
                                {['車牌號碼', '預約時間'].map((v) => (
                                    <Typography key={v} component={'div'} sx={{ ...rowStyle }}>
                                        {v}
                                    </Typography>
                                ))}
                                <Typography component={'div'} sx={{ ...rowStyle, position: 'relative' }}>
                                    {'操作'}
                                </Typography>
                                {props.carQueues.map((carQueue) => {
                                    return (
                                        <Fragment key={carQueue.carQueueId!}>
                                            <Typography component={'div'} sx={{ ...rowStyle }}>
                                                {carQueue.carId}
                                            </Typography>
                                            <Typography component={'div'} sx={{ ...rowStyle }}>
                                                {carQueue.estimateTime ? moment(carQueue.estimateTime).format('YYYY-MM-DD A hh:mm') : ''}
                                            </Typography>
                                            <Typography component={'div'} sx={{ ...rowStyle }}>
                                                <Grid container justifyContent={'space-around'}>
                                                    <Button
                                                        variant='contained'
                                                        className={props.classes.buttonOrange}
                                                        onClick={() =>
                                                            props.page.alert.show({
                                                                type: 'warning',
                                                                title: '警告',
                                                                message: '確定要取消？',
                                                                onConfirm: async () => {
                                                                    props.page.setIsLoading(true);
                                                                    const ret = await api.carQueue
                                                                        .update({ carQueueId: carQueue.carQueueId!, cancel: 'Y' })
                                                                        .finally(() => props.page.setIsLoading(false));
                                                                    if (ret.success) {
                                                                        props.reloadMyCarQueues();
                                                                        // props.reloadTimelineArray();
                                                                        props.page.simpleAlert.show({
                                                                            autoHideDuration: 2000,
                                                                            severity: 'success',
                                                                            message: ret.message,
                                                                        });
                                                                    } else {
                                                                        props.page.simpleAlert.show({
                                                                            autoHideDuration: 6000,
                                                                            severity: 'error',
                                                                            message: JSON.stringify(ret),
                                                                        });
                                                                    }
                                                                },
                                                            })
                                                        }
                                                    >
                                                        {'取消'}
                                                    </Button>
                                                </Grid>
                                            </Typography>
                                        </Fragment>
                                    );
                                })}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <ToReservationDialog {...props.toReservationDialog} />
        </Page>
    );
};
